import { List } from "antd";
import { type ReactNode } from "react";

import { IconArrowDownUp } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import {
    BTIconCalendarOutlined,
    BTIconCheckOutlined,
    BTIconNewHome,
    BTIconSortAscendingOutlined,
    BTIconSortDescendingOutlined,
} from "commonComponents/btWrappers/BTIcon";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { JobSortOptions } from "commonComponents/utilities/JobPicker/JobPicker.types";

import "./JobPickerSortPopover.less";

interface IJobPickerSortPopoverProps {
    showPopover: boolean;
    sortOption: JobSortOptions;
    onVisibleChange: (visible: boolean) => void;
    onSortChanged: (sortOption: JobSortOptions) => Promise<void>;
}

interface ISortOption {
    icon: ReactNode;
    text: string;
    value: JobSortOptions;
}

const SORT_OPTIONS: ISortOption[] = [
    {
        text: "Alphabetically",
        icon: <BTIconSortAscendingOutlined />,
        value: JobSortOptions.Alphabetic,
    },
    {
        text: "Reverse alphabetically",
        icon: <BTIconSortDescendingOutlined />,
        value: JobSortOptions.ReverseAlphabetic,
    },
    {
        text: "Projected start date",
        icon: <BTIconCalendarOutlined />,
        value: JobSortOptions.StartDateProjected,
    },
    {
        text: "Projected end date",
        icon: <BTIconCalendarOutlined />,
        value: JobSortOptions.CloseDate,
    },
    {
        text: "Created date",
        icon: <BTIconNewHome />,
        value: JobSortOptions.CreatedDate,
    },
];

export function JobPickerSortPopover({
    showPopover,
    sortOption,
    onVisibleChange,
    onSortChanged,
}: IJobPickerSortPopoverProps) {
    return (
        <BTPopover
            className="ActionIcon"
            content={
                <List<ISortOption>
                    dataSource={SORT_OPTIONS}
                    header="Sort Jobs"
                    renderItem={(item) => (
                        <List.Item
                            onClick={async () => {
                                onVisibleChange(false);
                                await onSortChanged(item.value);
                            }}
                        >
                            <BTRow className="w-full" responsiveMode="viewport" align="middle">
                                <BTCol span={4}>{item.icon}</BTCol>
                                <BTCol span={16}>{item.text}</BTCol>
                                <BTCol span={4}>
                                    {sortOption === item.value && <BTIconCheckOutlined />}
                                </BTCol>
                            </BTRow>
                        </List.Item>
                    )}
                />
            }
            placement="rightTop"
            trigger="click"
            visible={showPopover}
            onVisibleChange={onVisibleChange}
            overlayClassName="SortJobOverlay"
        >
            <BTButton type="tertiary" data-testid="jobPickerSort" icon={<IconArrowDownUp />} />
        </BTPopover>
    );
}
